import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {SupplierUser} from '../core/_models'
import {UserInfoCell} from './UserInfoCell'
import {UserActionsCell} from './UserActionsCell'
import {CustomHeaderColumn} from './CustomHeaderColumn'

interface UsersTableProps {
  users: SupplierUser[]
  selectedUsers: number[]
  onSelectionChange: (users: number[]) => void
  onEditUser: (user: SupplierUser) => void
  onDeleteUser: (userId: number) => void
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  selectedUsers,
  onSelectionChange,
  onEditUser,
  onDeleteUser,
}) => {
  const intl = useIntl()

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      onSelectionChange([])
    } else {
      onSelectionChange(users.map(user => user.supplier_user_id))
    }
  }

  const handleSelectUser = (userId: number) => {
    if (selectedUsers.includes(userId)) {
      onSelectionChange(selectedUsers.filter(id => id !== userId))
    } else {
      onSelectionChange([...selectedUsers, userId])
    }
  }

  return (
    <div className='table-responsive' style={{marginTop: '20px'}}>
      <table className='table' style={{
        borderCollapse: 'separate', 
        borderSpacing: '0', 
        border: '1px solid #dee2e6', 
        borderRadius: '8px', 
        overflow: 'hidden',
        width: '100%'
      }}>
        <thead style={{
          backgroundColor: '#f8f9fa', 
          borderBottom: '3px solid #007bff',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <tr style={{
            height: '60px',
            borderBottom: '3px solid #007bff'
          }}>
            <th style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '3px solid #007bff',
              padding: '15px 12px',
              fontWeight: 'bold',
              fontSize: '12px',
              textTransform: 'uppercase',
              color: '#495057',
              width: '50px'
            }}>
              <div className='form-check form-check-sm form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={selectedUsers.length === users.length && users.length > 0}
                  onChange={handleSelectAll}
                />
              </div>
            </th>
            <th style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '3px solid #007bff',
              padding: '15px 12px',
              fontWeight: 'bold',
              fontSize: '12px',
              textTransform: 'uppercase',
              color: '#495057',
              textAlign: 'left'
            }}>
              {intl.formatMessage({id: 'USERS.USER_DETAILS'})}
            </th>
            <th style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '3px solid #007bff',
              padding: '15px 12px',
              fontWeight: 'bold',
              fontSize: '12px',
              textTransform: 'uppercase',
              color: '#495057',
              textAlign: 'left'
            }}>
              {intl.formatMessage({id: 'USERS.ROLE'})}
            </th>
            <th style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '3px solid #007bff',
              padding: '15px 12px',
              fontWeight: 'bold',
              fontSize: '12px',
              textTransform: 'uppercase',
              color: '#495057',
              textAlign: 'left'
            }}>
              {intl.formatMessage({id: 'USERS.CREATED_BY'})}
            </th>
            <th style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '3px solid #007bff',
              padding: '15px 12px',
              fontWeight: 'bold',
              fontSize: '12px',
              textTransform: 'uppercase',
              color: '#495057',
              textAlign: 'left'
            }}>
              {intl.formatMessage({id: 'USERS.CREATED_AT'})}
            </th>
            <th style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '3px solid #007bff',
              padding: '15px 12px',
              fontWeight: 'bold',
              fontSize: '12px',
              textTransform: 'uppercase',
              color: '#495057',
              textAlign: 'left'
            }}>
              {intl.formatMessage({id: 'USERS.STATUS'})}
            </th>
            <th style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '3px solid #007bff',
              padding: '15px 12px',
              fontWeight: 'bold',
              fontSize: '12px',
              textTransform: 'uppercase',
              color: '#495057',
              textAlign: 'right',
              minWidth: '100px'
            }}>
              {intl.formatMessage({id: 'USERS.ACTIONS'})}
            </th>
          </tr>
        </thead>
        <tbody style={{backgroundColor: '#ffffff'}}>
          {users.map((user) => (
            <tr key={user.supplier_user_id} style={{
              height: '70px',
              borderBottom: '1px solid #e9ecef',
              backgroundColor: selectedUsers.includes(user.supplier_user_id) ? '#f8f9fa' : '#ffffff'
            }}>
              <td style={{
                padding: '15px 12px',
                borderBottom: '1px solid #e9ecef',
                backgroundColor: selectedUsers.includes(user.supplier_user_id) ? '#f8f9fa' : '#ffffff'
              }}>
                <div className='form-check form-check-sm form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={selectedUsers.includes(user.supplier_user_id)}
                    onChange={() => handleSelectUser(user.supplier_user_id)}
                  />
                </div>
              </td>
              <td style={{
                padding: '15px 12px',
                borderBottom: '1px solid #e9ecef',
                backgroundColor: selectedUsers.includes(user.supplier_user_id) ? '#f8f9fa' : '#ffffff'
              }}>
                <UserInfoCell user={user} />
              </td>
              <td style={{
                padding: '15px 12px',
                borderBottom: '1px solid #e9ecef',
                backgroundColor: selectedUsers.includes(user.supplier_user_id) ? '#f8f9fa' : '#ffffff'
              }}>
                <span className={`badge badge-${user.role === 'admin' ? 'success' : 'primary'}`}>
                  {intl.formatMessage({id: `USERS.${user.role.toUpperCase()}_ROLE`})}
                </span>
              </td>
              <td style={{
                padding: '15px 12px',
                borderBottom: '1px solid #e9ecef',
                backgroundColor: selectedUsers.includes(user.supplier_user_id) ? '#f8f9fa' : '#ffffff'
              }}>
                {user.created_by_name || '-'}
              </td>
              <td style={{
                padding: '15px 12px',
                borderBottom: '1px solid #e9ecef',
                backgroundColor: selectedUsers.includes(user.supplier_user_id) ? '#f8f9fa' : '#ffffff'
              }}>
                {new Date(user.created_at).toLocaleDateString()}
              </td>
              <td style={{
                padding: '15px 12px',
                borderBottom: '1px solid #e9ecef',
                backgroundColor: selectedUsers.includes(user.supplier_user_id) ? '#f8f9fa' : '#ffffff'
              }}>
                <span className={`badge badge-${user.is_active ? 'success' : 'danger'}`}>
                  {intl.formatMessage({id: `USERS.${user.is_active ? 'ACTIVE' : 'INACTIVE'}`})}
                </span>
              </td>
              <td style={{
                padding: '15px 12px',
                borderBottom: '1px solid #e9ecef',
                backgroundColor: selectedUsers.includes(user.supplier_user_id) ? '#f8f9fa' : '#ffffff',
                textAlign: 'right'
              }}>
                <UserActionsCell
                  user={user}
                  onEdit={onEditUser}
                  onDelete={onDeleteUser}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export {UsersTable}
